import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { ReportsService } from '@modules/planner/services/reports/reports.service';
import { DateUtils } from '@shared/services/dateUtils/date-utils.service';
import { Report } from '@shared/models/report';

export interface EditableReport extends Report {
  firstname?: string;
  lastname?: string;
}

@Component({
  selector: 'app-edit-report-dialog',
  templateUrl: './edit-report-dialog.component.html',
  styleUrls: ['./edit-report-dialog.component.scss'],
  standalone: false
})
export class EditReportDialogComponent implements OnChanges {

  @Input() report!: EditableReport;
  @Input() phaseName: string | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() changed = new EventEmitter<void>();

  notes = '';
  name = '';
  identifier = '';
  interruptCode = '';
  attendTime = '';
  reportType = 0;
  saving = false;

  // Remark (4) can't be converted
  readonly editableTypes = [
    { id: 1, label: 'planner.projectDashboard.editReport.typeDone' },
    { id: 2, label: 'planner.projectDashboard.editReport.typeInterrupted' },
    { id: 3, label: 'planner.projectDashboard.editReport.typeDeviation' },
  ];

  constructor(
    private reportsService: ReportsService,
    private translateService: TranslateService,
  ) { }

  ngOnChanges(): void {
    if (!this.report) return;
    this.notes = this.report.notes ?? '';
    this.name = this.report.name ?? '';
    this.identifier = this.report.identifier ?? '';
    this.interruptCode = this.report.interrupt_code ?? '';
    this.attendTime = this.toInputDateTime(this.report.attend_time);
    this.reportType = Number(this.report.report_type);
  }

  get canChangeType(): boolean {
    return this.editableTypes.some(type => type.id === Number(this.report?.report_type));
  }

  get typeChanged(): boolean {
    return this.reportType !== Number(this.report?.report_type);
  }

  get reportTypeLabel(): string {
    const key = {
      1: 'planner.projectDashboard.editReport.typeDone',
      2: 'planner.projectDashboard.editReport.typeInterrupted',
      3: 'planner.projectDashboard.editReport.typeDeviation',
      4: 'planner.projectDashboard.editReport.typeAttention',
    }[this.report?.report_type];
    return key ? this.translateService.instant(key) : String(this.report?.report_type ?? '');
  }

  get isRemarkReport(): boolean {
    return Number(this.report?.report_type) === 4;
  }

  // Field reporting never sends identifier, so only show existing ones
  get showIdentifier(): boolean {
    return !!this.report?.identifier;
  }

  get showInterruptCode(): boolean {
    return this.reportType === 2 || !!this.report?.interrupt_code;
  }

  formatDateTime(value: string | null | undefined): string {
    return DateUtils.formatUtcToLocal(value, 'datetime');
  }

  save(): void {
    if (this.saving) return;
    this.saving = true;
    // Only send visible fields so hidden values are never cleared
    const data: Partial<Report> = {
      notes: this.notes,
      attend_time: this.fromInputDateTime(this.attendTime),
    };
    if (this.isRemarkReport) data.name = this.name;
    if (this.showIdentifier) data.identifier = this.identifier;
    if (this.showInterruptCode) data.interrupt_code = this.interruptCode;
    if (this.typeChanged) data.report_type = this.reportType;

    this.reportsService.updateReport(this.report.id, data).subscribe(result => {
      this.saving = false;
      if (result !== null) {
        this.changed.emit();
        this.closed.emit();
      }
    });
  }

  remove(): void {
    if (this.saving) return;
    Swal.fire({
      title: this.translateService.instant('planner.projectDashboard.editReport.removeConfirmation'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: this.translateService.instant('basic.yes'),
      cancelButtonText: this.translateService.instant('basic.cancel'),
    }).then(result => {
      if (!result.isConfirmed) return;
      this.saving = true;
      this.reportsService.removeReport(this.report.id).subscribe(removed => {
        this.saving = false;
        if (removed !== null) {
          this.changed.emit();
          this.closed.emit();
        }
      });
    });
  }

  close(): void {
    this.closed.emit();
  }

  // No timezone conversion: field reporting stores attend_time in local time
  private toInputDateTime(value: string | null): string {
    if (!value) return '';
    return value.replace(' ', 'T').substring(0, 16);
  }

  private fromInputDateTime(value: string): string | null {
    if (!value) return null;
    return value.replace('T', ' ') + (value.length === 16 ? ':00' : '');
  }
}
